/* eslint-disable camelcase */
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import InvoicePicker from './InvoicePicker'

import styles from '@/pages/app/BillingOps/styles.module.scss'

import { InvoiceSummary } from '@/api/generated'
import Input from '@/components/Input'
import { AddPoToInvoiceInput } from '@/pages/app/BillingOps/types'

const schema = z.object({
  invoice_id: z.string().min(1, 'Invoice ID is required').startsWith('in_', 'Invoice IDs start with "in_"'),
  po_number: z.string().min(1, 'PO number is required').max(140, 'PO number must be 140 characters or fewer'),
})

type FormValues = z.infer<typeof schema>

interface AddPoToInvoiceFormProps {
  disabled: boolean
  onSubmit: (input: AddPoToInvoiceInput) => void
  onChange: () => void
  formId: string
  draftInvoice: InvoiceSummary | null | undefined
  openInvoice: InvoiceSummary | null | undefined
}

const AddPoToInvoiceForm = ({
  disabled,
  onSubmit,
  onChange,
  formId,
  draftInvoice,
  openInvoice,
}: AddPoToInvoiceFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { invoice_id: '', po_number: '' },
  })

  const currentInvoiceId = useWatch({ control, name: 'invoice_id' }) ?? ''
  const willRevise = openInvoice !== null && openInvoice !== undefined && currentInvoiceId === openInvoice.id

  const submit = handleSubmit((values) => onSubmit(values))

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      className={styles.section}
    >
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="add-po-invoice-id">
          Invoice ID
        </label>
        <InvoicePicker
          draft={draftInvoice}
          open={openInvoice}
          selectedId={currentInvoiceId || null}
          onSelect={(invoice) => {
            setValue('invoice_id', invoice.id, { shouldValidate: true })
            onChange()
          }}
        />
        <Controller
          name="invoice_id"
          control={control}
          render={({ field }) => (
            <Input
              id="add-po-invoice-id"
              type="text"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="in_1AbCdEfGhIjKlMnO"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        {errors.invoice_id && <p className={styles.errorText}>{errors.invoice_id.message}</p>}
        {willRevise && (
          <div className={styles.warningBanner}>
            This invoice is finalized. <code>add_po_to_invoice</code> only operates on drafts — use{' '}
            <code>recreate_subscription_invoice</code> instead.
          </div>
        )}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="add-po-number">
          PO Number
        </label>
        <Controller
          name="po_number"
          control={control}
          render={({ field }) => (
            <Input
              id="add-po-number"
              type="text"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="PO-2026-0001"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        {errors.po_number && <p className={styles.errorText}>{errors.po_number.message}</p>}
      </div>
    </form>
  )
}

export default AddPoToInvoiceForm
